/**
 * Memento pattern — undo/redo for the retro canvas.
 *
 * Each history entry is a full snapshot of the canvas state (stickies +
 * text labels) taken immediately BEFORE a mutation.  This is the Memento
 * pattern: the hook stores opaque state objects and restores them on demand
 * without knowing anything about the mutations themselves.
 *
 * Two stacks model the timeline:
 *
 *   past   [ s0, s1, s2 ]   ← grows with every mutation
 *   future [ s3 ]           ← populated by undo; cleared by any new mutation
 *
 * On undo: pop past → return as restored state, push current → future.
 * On redo: shift future → return as restored state, push current → past.
 * On new action: push to past, clear future (linear timeline, no branching).
 *
 * The caller (CanvasSurface) owns the live state (stickies / textLabels via
 * useState).  This hook is a side-car that holds only the history; it never
 * duplicates the live state.  Callers pass the current state into undo() /
 * redo() so the hook can stash it on the opposite stack — this avoids storing
 * a redundant "present" copy inside the hook itself.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { StickyState, TextLabelState } from '../api/Requests';

const MAX_HISTORY_ENTRIES = 50;

export interface HistoryEntry {
  stickies: StickyState[];
  textLabels: TextLabelState[];
}

export interface UseCanvasHistoryResult {
  /**
   * Snapshot the canvas state BEFORE a mutation so it can be restored by undo.
   * Clears the redo stack (future) because a new action creates a new timeline.
   */
  pushHistory: (stickies: StickyState[], textLabels: TextLabelState[]) => void;

  /**
   * Move one step backward in history.
   * The caller must pass the current canvas state so it can be placed onto the
   * redo stack, making it recoverable with redo().
   * Returns the state to restore, or null if nothing can be undone.
   */
  undo: (currentStickies: StickyState[], currentTextLabels: TextLabelState[]) => HistoryEntry | null;

  /**
   * Move one step forward in history.
   * The caller must pass the current canvas state so it can be placed back onto
   * the undo stack.
   * Returns the state to restore, or null if nothing can be redone.
   */
  redo: (currentStickies: StickyState[], currentTextLabels: TextLabelState[]) => HistoryEntry | null;

  /**
   * Look at the next undo snapshot without mutating the stacks.
   * Used to abort canvas undo when a restore would touch an item someone else holds.
   */
  peekUndo: () => HistoryEntry | null;

  /**
   * Look at the next redo snapshot without mutating the stacks.
   */
  peekRedo: () => HistoryEntry | null;

  canUndo: boolean;
  canRedo: boolean;
}

export function useCanvasHistory(): UseCanvasHistoryResult {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // Refs (not state) so mutations to the stacks never trigger re-renders;
  // only the boolean canUndo/canRedo flags need to cause UI updates.
  const pastRef   = useRef<HistoryEntry[]>([]);
  const futureRef = useRef<HistoryEntry[]>([]);

  const pushHistory = useCallback((stickies: StickyState[], textLabels: TextLabelState[]) => {
    const past    = pastRef.current;
    const trimmed = past.length >= MAX_HISTORY_ENTRIES ? past.slice(1) : past;
    pastRef.current   = [...trimmed, { stickies, textLabels }];
    futureRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
  }, []);

  const undo = useCallback(
    (currentStickies: StickyState[], currentTextLabels: TextLabelState[]): HistoryEntry | null => {
      const past = pastRef.current;
      if (past.length === 0) return null;

      const entry       = past[past.length - 1];
      pastRef.current   = past.slice(0, -1);
      // Stash the current (pre-undo) state as the head of the redo stack.
      futureRef.current = [{ stickies: currentStickies, textLabels: currentTextLabels }, ...futureRef.current];

      setCanUndo(pastRef.current.length > 0);
      setCanRedo(true);
      return entry;
    },
    [],
  );

  const redo = useCallback(
    (currentStickies: StickyState[], currentTextLabels: TextLabelState[]): HistoryEntry | null => {
      const future = futureRef.current;
      if (future.length === 0) return null;

      const entry       = future[0];
      futureRef.current = future.slice(1);
      // Stash the current (pre-redo) state back onto the past stack.
      pastRef.current   = [...pastRef.current, { stickies: currentStickies, textLabels: currentTextLabels }];

      setCanUndo(true);
      setCanRedo(futureRef.current.length > 0);
      return entry;
    },
    [],
  );

  const peekUndo = useCallback((): HistoryEntry | null => {
    const past = pastRef.current;
    if (past.length === 0) return null;
    return past[past.length - 1];
  }, []);

  const peekRedo = useCallback((): HistoryEntry | null => {
    const future = futureRef.current;
    if (future.length === 0) return null;
    return future[0];
  }, []);

  // Memoize the returned object so its reference is stable across renders.
  // pushHistory/undo/redo are useCallback([]) and never change; this object
  // therefore only changes when canUndo or canRedo actually flip — not on
  // every render. Callers can safely include `history` in useCallback/useEffect
  // dependency arrays without recreating callbacks on every render.
  return useMemo(
    () => ({ pushHistory, undo, redo, peekUndo, peekRedo, canUndo, canRedo }),
    [pushHistory, undo, redo, peekUndo, peekRedo, canUndo, canRedo],
  );
}
